"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.suggestStatementMatches = suggestStatementMatches;
exports.statementLineKeys = statementLineKeys;
const aging_1 = require("../sales/aging");
const money_1 = require("../money");
const DEFAULT_TOLERANCE_DAYS = 3;
// الدرجات ترتيب لا احتمال: المبلغ شرط، والتاريخ ورقم المستند يفاضلان بين المرشحين
const AMOUNT_SCORE = 50;
const SAME_DATE_SCORE = 30;
const PER_DAY_PENALTY = 8;
const DOCUMENT_SCORE = 40;
/** أقصر مرجع يُبحث عنه داخل نص الكشف — «12» يظهر فى أى نص صدفةً. */
const MIN_TOKEN_LENGTH = 4;
const INDIC = /[٠-٩]/g;
const PERSIAN = /[۰-۹]/g;
/** نص للمقارنة: أرقام لاتينية، حروف صغيرة، بلا مسافات ولا فواصل. */
function normalizeText(value) {
    return value
        .replace(INDIC, (digit) => String(digit.charCodeAt(0) - 0x0660))
        .replace(PERSIAN, (digit) => String(digit.charCodeAt(0) - 0x06f0))
        .toLowerCase()
        .replace(/[\s\-_/.,:#]+/g, '');
}
/** هل يظهر رقم القيد أو مرجعه فى بيان الكشف أو مرجعه؟ */
function mentionsDocument(statement, ledger) {
    const haystack = normalizeText(statement.description + ' ' + (statement.reference ?? ''));
    return [ledger.journalNumber, ledger.reference]
        .filter((token) => !!token)
        .map(normalizeText)
        .some((token) => token.length >= MIN_TOKEN_LENGTH && haystack.includes(token));
}
/**
 * يقترح لكل سطر كشف سطر الدفتر المقابل: المبلغ بإشارته يساوى تمامًا، والتاريخ فى حدود
 * الهامش (الشيك يُصرف بعد إصداره بأيام). يرتفع المرشح بقرب التاريخ وبظهور رقم المستند فى
 * بيان البنك. ثم يوزَّع المقترح واحدًا لواحد بالأعلى درجة أولاً — سطر الدفتر لا يُقترح
 * لسطرى كشف. اقتراح فقط: القبول للمستخدم، والإتمام يتحقق منه `reconcileBank`.
 */
function suggestStatementMatches(statement, ledger, options = {}) {
    const tolerance = options.toleranceDays ?? DEFAULT_TOLERANCE_DAYS;
    const ledgerNet = ledger.map((line) => ({
        line,
        net: money_1.Money.of(line.debit).sub(line.credit),
    }));
    const matches = statement.map((item) => {
        const amount = money_1.Money.of(item.amount);
        const candidates = [];
        for (const { line, net } of ledgerNet) {
            if (!net.eq(amount))
                continue;
            const dayGap = (0, aging_1.daysBetween)(line.postingDate, item.date);
            if (Math.abs(dayGap) > tolerance)
                continue;
            const reasons = ['AMOUNT'];
            let score = AMOUNT_SCORE;
            if (dayGap === 0) {
                reasons.push('SAME_DATE');
                score += SAME_DATE_SCORE;
            }
            else {
                reasons.push('NEAR_DATE');
                score += SAME_DATE_SCORE - PER_DAY_PENALTY * Math.abs(dayGap);
            }
            if (mentionsDocument(item, line)) {
                reasons.push('DOCUMENT_NUMBER');
                score += DOCUMENT_SCORE;
            }
            candidates.push({ journalLineId: line.id, score, dayGap, reasons });
        }
        candidates.sort((a, b) => b.score - a.score ||
            Math.abs(a.dayGap) - Math.abs(b.dayGap) ||
            a.journalLineId.localeCompare(b.journalLineId));
        return { statementLineId: item.id, candidates, suggested: null };
    });
    // توزيع جشع ثابت الترتيب: الأعلى درجة أولاً، والتعادل بترتيب الكشف ثم الدفتر
    const pairs = matches
        .flatMap((match, statementIndex) => match.candidates.map((candidate) => ({ match, statementIndex, candidate })))
        .sort((a, b) => b.candidate.score - a.candidate.score ||
        a.statementIndex - b.statementIndex ||
        a.candidate.journalLineId.localeCompare(b.candidate.journalLineId));
    const taken = new Set();
    for (const { match, candidate } of pairs) {
        if (match.suggested !== null || taken.has(candidate.journalLineId))
            continue;
        match.suggested = candidate.journalLineId;
        taken.add(candidate.journalLineId);
    }
    return matches;
}
/**
 * مفتاح منع الاستيراد المكرر: التاريخ والمبلغ المطبَّع والمرجع، ثم ترتيب تكرار السطر
 * المطابق داخل نفس الملف. فإعادة استيراد نفس الكشف (أو كشف متداخل معه) لا تضيف شيئًا،
 * وسطران حقيقيان متطابقان فى يوم واحد (رسمان بنكيان بلا مرجع) يبقيان سطرين.
 */
function statementLineKeys(lines) {
    const seen = new Map();
    return lines.map((line) => {
        const base = [
            line.date,
            money_1.Money.of(line.amount).toFixed(4),
            normalizeText(line.reference ?? ''),
        ].join('|');
        const occurrence = (seen.get(base) ?? 0) + 1;
        seen.set(base, occurrence);
        return base + '|' + occurrence;
    });
}
//# sourceMappingURL=statement-matching.js.map